<template>
  <!-- Breadcrumb -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" />
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <router-link :to="{ name: 'pages.admin.videos' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.videos", "Videos") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("common.edit", "Edit") }}</span>
    </button>
  </div>

  <div class="tw-p-2">
    <div class="tw-w-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-200">
      <div class="tw-mb-8">
        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">{{ $t('videos.edit-title', 'Edit video') }}</h1>
        <p class="tw-text-sm tw-text-gray-600">{{ $t('videos.edit-subtitle', 'Update this video') }}</p>
      </div>

      <div v-if="loading" class="tw-py-16 tw-text-center tw-text-gray-400">
        <icon icon="eos-icons:three-dots-loading" class="tw-text-4xl" />
      </div>

      <VideoForm
        v-else
        ref="formRef"
        :video="video"
        :saving="saving"
        :submit-label="$t('videos.update-button', 'Save changes')"
        @submit="updateVideo"
        @cancel="goBack"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import VideoForm from './partials/VideoForm.vue';
import videosApi from '@/api/videos';
import useAlert from '@/composables/useAlert';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const video = ref(null);
const formRef = ref(null);

const goBack = () => router.push({ name: 'pages.admin.videos' });

const fetchVideo = async () => {
    loading.value = true;
    try {
        const response = await videosApi.admin.edit(route.params.id);
        video.value = response.data?.data || null;
    } catch (error) {
        console.error('Error loading video:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        goBack();
    } finally {
        loading.value = false;
    }
};

const updateVideo = async (formData) => {
    saving.value = true;
    try {
        await videosApi.admin.update(route.params.id, formData);
        useAlert(t('videos.updated', 'Video updated successfully'), 'success');
        router.push({ name: 'pages.admin.videos' });
    } catch (error) {
        console.error('Error updating video:', error);
        if (error.response?.status === 422) {
            formRef.value?.setErrors(error.response.data?.errors || {});
            useAlert(t('videos.fix-errors', 'Please fix the form errors'), 'warning');
        } else {
            useAlert(error.response?.data?.message || t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        }
    } finally {
        saving.value = false;
    }
};

onMounted(fetchVideo);
</script>
